import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Close as CloseIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon 
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import consultationService from '../services/consultationService';

const ConsultationModal = ({ open, onClose, reception, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    type: 'clinique',
    prix: '',
  });
  const [status, setStatus] = useState({ type: '', message: '', details: undefined });

  const handleClose = () => {
    setFormData({
      label: '',
      description: '',
      type: 'clinique',
      prix: '',
    });
    setStatus({ type: '', message: '', details: undefined });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '', details: undefined });
    
    try {
      const consultationData = {
        ...formData,
        reception_id: reception.id,
        partenaire_id: reception.partenaire_id,
        prix: parseFloat(formData.prix)
      };

      console.log('Données envoyées:', consultationData); // Debug

      const response = await consultationService.createConsultation(consultationData);
      console.log('Réponse du service:', response); // Debug
      
      if (response.success) {
        toast.success('Consultation créée avec succès');
        if (onSuccess) onSuccess(response.data);
        handleClose();
      } else {
        let errorMessage = response.message;
        if (response.error?.details) {
          errorMessage += '\n' + Object.values(response.error.details).join('\n');
        }
        
        setStatus({
          type: 'error',
          message: errorMessage,
          details: response.error?.details
        });
      }
    } catch (error) {
      console.error('Erreur complète:', error);
      setStatus({
        type: 'error',
        message: 'Une erreur est survenue lors de la création',
        details: error.message ? [error.message] : undefined
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">Nouvelle Consultation</Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {status.type && (
            <Alert 
              severity={status.type} 
              sx={{ mb: 2 }}
            >
              {status.message}
              {status.details && (
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {Array.isArray(status.details) 
                    ? status.details.map((detail, index) => (
                        <li key={index}>{detail}</li>
                      ))
                    : Object.entries(status.details).map(([key, value]) => (
                        <li key={key}>{`${key}: ${value}`}</li>
                      ))
                  }
                </Box>
              )}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Label"
            value={formData.label}
            onChange={(e) => setFormData({...formData, label: e.target.value})}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              label="Type"
              onChange={(e) => setFormData({...formData, type: e.target.value})}
            >
              <MenuItem value="clinique">Clinique</MenuItem>
              <MenuItem value="clinique_labo">Clinique + Labo</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Prix"
            type="number"
            value={formData.prix}
            onChange={(e) => setFormData({...formData, prix: e.target.value})}
            required
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Création...' : 'Créer la consultation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ConsultationModal;
